
import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useCompanyProfile, CompanyProfile } from '@/hooks/useCompanyProfile';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { useForm } from 'react-hook-form';

const CompanyInfoForm: React.FC = () => {
  const { companyProfile, updateCompanyProfile, isLoading } = useCompanyProfile();

  // Create form with react-hook-form
  const form = useForm<CompanyProfile>({
    // Provide empty defaults if companyProfile is null
    defaultValues: companyProfile || {
      name: '',
      taxNumber: '',
      registrationNumber: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        countryCode: 'HU'
      },
      description: '',
    }
  });

  // Update form when companyProfile changes
  useEffect(() => {
    if (companyProfile) {
      console.log('Initializing company info form with data:', companyProfile);
      form.reset(companyProfile);
    }
  }, [companyProfile, form]);

  const onSubmit = (data: CompanyProfile) => {
    console.log('Submitting form data:', data);
    updateCompanyProfile(data);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Céges adatok</CardTitle>
        <CardDescription>Adja meg a szervezői céges adatokat</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Cég neve *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      required
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="taxNumber"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Adószám</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="12345678-1-23"
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registrationNumber"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Cégjegyzékszám</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="01-09-123456"
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="address.postalCode"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Irányítószám</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="1234"
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.city"
                render={({ field }) => (
                  <FormItem className="space-y-2 md:col-span-2">
                    <FormLabel>Település</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Budapest"
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address.street"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Utca, házszám</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Példa utca 123."
                      value={field.value || ''}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Cég rövid leírása</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Rövid bemutatkozás a cégéről..."
                      rows={4}
                      value={field.value || ''}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full md:w-auto"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Mentés...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Céges adatok mentése
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default CompanyInfoForm;
